'use client';

import { CartMethodOptions } from '@eliadmin/core/src/components/cart/detail/cart-method-options';
import { CartShipmentsStatus } from '@eliadmin/core/src/components/cart/detail/cart-shipments-status';
import { hasStoredMethodSelection } from '@eliadmin/core/src/utils/cart-method-selection';
import { cartShipmentSelectionMode } from '@eliadmin/core/src/api/cart/cart-api';

import { CartGiftSection } from '@eliadmin/core/src/components/cart/detail/cart-gift-section';

import Link from '@eliadmin/core/src/components/link';

import { type CartShipment as CartShipmentType } from '@eliadmin/core/src/api/cart/cart-types';
import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';

import { CacheKey } from '@eliadmin/core/src/api/constants';

import { CartSummaryCard } from '@eliadmin/core/src/components/cart/cart-summary-card';
import { CartItem } from '@eliadmin/core/src/components/cart/detail/cart-item';
import { CartPayment } from '@eliadmin/core/src/components/cart/detail/cart-payment';
import { CartDistanceDestinationFields } from '@eliadmin/core/src/components/cart/detail/cart-distance-destination-fields';
import { CartShipment } from '@eliadmin/core/src/components/cart/detail/cart-shipment';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartActions } from '@eliadmin/core/src/hooks/cart/use-cart-actions';
import { useCartPaymentsState } from '@eliadmin/core/src/hooks/cart/use-cart-payments-state';
import { useCartShipments } from '@eliadmin/core/src/hooks/cart/use-cart-shipments';
import { useCartShipmentDestination } from '@eliadmin/core/src/hooks/cart/use-cart-shipment-destination';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CartStep } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { CartSection } from '../cart-section';
import { CartNavigationButton } from '../continue-button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

import { CartApplyCoupon } from './cart-apply-coupon';
import { CartApplyLoyaltyPoints } from './cart-apply-loyalty-points';
import { Switch } from '../../ui/switch';
import { MdKeyboardArrowLeft, MdOutlineRedeem, MdOutlineWorkspacePremium } from 'react-icons/md';
import { Heading } from '@eliadmin/core/src/components/ui/heading';

export function CartDetails() {
  const { t } = useTranslations();

  const { data: cart } = useCart();

  const [couponOpen, setCouponOpen] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);

  return (
    <><>
      <CartSection>
        <div className="flex w-full flex-col items-center justify-center gap-4 sm:gap-5 lg:gap-6">
          <div
            className={cn('custom-container', 'flex flex-col items-center justify-center gap-4')}
          >
            <div className="hidden w-full items-center justify-start gap-6 pl-3 pr-6 lg:flex">
              {/* Column widths must match `CartItem`'s desktop row. The old fixed minimums
                  (450/600 + 2×248 px) needed ~1230px and clipped the total and the trash icon
                  between 1024 and ~1420px. */}
              <p className="min-w-0 flex-1 whitespace-nowrap text-sm font-bold tracking-tight text-[#666666]">
                {t('text20')}
              </p>
              <p className="w-[124px] shrink-0 whitespace-nowrap text-sm font-bold tracking-tight text-[#666666]">
                {t('text22')}
              </p>
              <p className="w-[150px] shrink-0 whitespace-nowrap text-sm font-bold tracking-tight text-[#666666] xl:w-[200px]">
                {t('text662')}
              </p>
              <p className="w-[150px] shrink-0 whitespace-nowrap text-sm font-bold tracking-tight text-[#666666] xl:w-[200px]">
                {t('text482')}
              </p>
              <div className="h-5 w-6 shrink-0 bg-transparent" />
            </div>
            {cart?.items?.map(item => (
              <CartItem key={item.id} item={item} />
            ))}
          </div>
          <div
            className={cn(
              'custom-container',
              'grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:gap-6',
            )}
          >
            <div className="col-span-1 flex h-fit w-full flex-col items-start justify-start rounded-[4px] border border-border p-3 sm:p-4 lg:p-6">
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-center justify-start gap-2 md:gap-3">
                  <MdOutlineRedeem className="max-h-6 min-h-6 min-w-6 max-w-6 text-primary sm:max-h-8 sm:min-h-8 sm:min-w-8 sm:max-w-8" />
                  <Heading tag="h3" className={{ title: 'h5' }} title={t('text647')} />
                </div>
                <Switch
                  checked={couponOpen}
                  onCheckedChange={setCouponOpen}
                  aria-label={t('text647')}
                />
              </div>
              <div
                className={cn(
                  'grid w-full transition-[grid-template-rows] duration-300 ease-in-out',
                  couponOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <div
                    className={cn(
                      'pt-5 transition-opacity duration-300 sm:pt-6',
                      couponOpen ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <CartApplyCoupon />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-1 flex h-fit w-full flex-col items-start justify-start rounded-[4px] border border-border p-3 sm:p-4 lg:p-6">
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-center justify-start gap-2 md:gap-3">
                  <MdOutlineWorkspacePremium className="max-h-6 min-h-6 min-w-6 max-w-6 text-primary sm:max-h-8 sm:min-h-8 sm:min-w-8 sm:max-w-8" />
                  <Heading tag="h3" className={{ title: 'h5' }} title={t('text1164')} />
                </div>
                <Switch
                  checked={loyaltyOpen}
                  onCheckedChange={setLoyaltyOpen}
                  aria-label={t('text1164')}
                />
              </div>
              <div
                className={cn(
                  'grid w-full transition-[grid-template-rows] duration-300 ease-in-out',
                  loyaltyOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <div
                    className={cn(
                      'pt-5 transition-opacity duration-300 sm:pt-6',
                      loyaltyOpen ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    <CartApplyLoyaltyPoints />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={cn(
              'custom-container',
              'grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:gap-6',
            )}
          >
            <div className="invisible col-span-1 h-[1px] w-full"></div>
            <Suspense fallback={<Skeleton className="h-64" />}>
              <div className="col-span-1 w-full">
                <CartSummaryCard itemsDisplayType="total" />
              </div>
            </Suspense>
          </div>
        </div>

        <div
          className={cn(
            'custom-container',
            'flex flex-col-reverse items-center justify-center gap-3 sm:flex-row sm:justify-end',
          )}
        >
          <Button asChild variant={'secondary'} className="w-full whitespace-nowrap sm:w-fit">
            <Link prefetch={false} href={ROUTES.HOME()}>
              <MdKeyboardArrowLeft size={20} /> {t('text480')}
            </Link>
          </Button>
          <CartNavigationButton
            step={CartStep.PAYMENT_SHIPMENT}
            title={t('text526')}
            className="w-full whitespace-nowrap sm:w-fit"
          />
        </div>
      </CartSection>
    </><CartGiftSection /></>
  );
}

export function CartShipments() {
  const shipmentState = useCartShipments();
  const { data: shipments } = shipmentState;
  const { data: cart } = useCart();
  const { setShipmentMutation } = useCartActions();
  const destination = useCartShipmentDestination();
  const locale = useLocaleStore(s => s.currentLocale);
  const countryId = destination.countryId;

  const [previousShipments, setPreviousShipments] = useState<CartShipmentType[]>([]);

  useEffect(() => {
    if (shipments?.length) {
      setPreviousShipments(shipments);
    }
  }, [shipments]);

  const [shipmentSet, setShipmentSet] = useState(false);
  const [countryIdChanged, setCountryIdChanged] = useState(false);
  const previousCountryId = useRef(countryId);

  useEffect(() => {
    setCountryIdChanged(previousCountryId.current !== countryId);
    previousCountryId.current = countryId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  useEffect(
    function setShipmentOnMount() {
      if (shipmentState.isUpdating) return;
      if (!shipmentState.isSuccess || shipmentState.isUpdating) return;
      if (cartShipmentSelectionMode !== 'explicit' && hasStoredMethodSelection(shipments)) return;
      const selected = shipments.find(shipment => String(shipment.id) === String(cart.shipment?.id));
      const shipment = selected ?? shipments[0];
      if (
        (!shipmentSet || countryIdChanged) &&
        !isNullish(shipment?.id) &&
        !isNullish(countryId) &&
        (shipment.price_type !== 'distance' || destination.isComplete)
      ) {
        setShipmentSet(true);
        setCountryIdChanged(false);
        // A step remount must not replace or reprice an already valid selection.
        if (selected && !countryIdChanged) return;
        setShipmentMutation.mutate({
          countryId: countryId,
          locale,
          shipmentId: shipment.id,
          body: shipment.price_type === 'distance' ? destination.body : undefined,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shipmentState.isUpdating, shipmentState.isSuccess, shipmentState.isUpdating, shipments, countryId, countryIdChanged, destination.key],
  );

  // Choose to render current shipments or fallback to previousShipments if shipments array is empty
  const shipmentsToRender = shipments;

  if (!shipmentState.hasCountry) return <CartShipmentsStatus status="country" />;
  if (shipmentState.isError) return <CartShipmentsStatus status="error" busy={shipmentState.isUpdating} onRetry={() => { void shipmentState.refetch(); }} />;
  if (shipmentState.isPending) return <CartShipmentsStatus status="loading" busy />;
  if (!shipments.length) return <CartShipmentsStatus status="empty" />;

  return (
    <CartMethodOptions disabled={shipmentState.isUpdating}>
      <div className="flex w-full flex-col items-start justify-start">
        <CartDistanceDestinationFields shipments={shipmentsToRender} className="w-full" />
        {shipmentsToRender.map((shipment: CartShipmentType) => (
          <CartShipment key={shipment.id} shipment={shipment} isLoading={!(shipments.length > 0)} />
        ))}
      </div>
    </CartMethodOptions>
  );
}

export function CartPayments() {
  const paymentState = useCartPaymentsState();
  const methodsUpdating = paymentState.isUpdating;
  const { data: payments } = paymentState;
  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const pendingShipments = useIsMutating({ mutationKey: [CacheKey.SHIPMENTS] });
  const pendingPayments = useIsMutating({ mutationKey: [CacheKey.CART_PAYMENTS] });
  const cartQuery = useCart();
  const { setPaymentTypeMutation } = useCartActions();
  const form = useFormContext<CreateOrderFormValues>();
  const locale = useLocaleStore(s => s.currentLocale);
  const lastAutomaticPayment = useRef<{
    payments: typeof payments;
    shipmentId: string;
    paymentId: string;
    countryId: string;
    locale: string;
  } | null>(null);

  useEffect(
    function refreshActivePaymentOnMount() {
      if (methodsUpdating || !payments?.length) return;
      if (hasStoredMethodSelection(payments ?? [])) return;
      if (!paymentState.isReady || queryClient.isMutating({ mutationKey: [CacheKey.SHIPMENTS] }) ||
        queryClient.isMutating({ mutationKey: [CacheKey.CART_PAYMENTS] })) return;
      const activePaymentId = cartQuery.data.paymentType?.id;
      const payment = !isNullish(activePaymentId)
        ? payments?.find(payment => String(payment.id) === String(activePaymentId))
        : undefined;
      const countryId = form.getValues(form.getValues('different_delivery_address') ? 'country2.id' : 'country.id');

      if (payment) {
        lastAutomaticPayment.current = null;
        return;
      }
      if (!payment && !!payments?.length && !isNullish(payments[0]?.id) && !isNullish(countryId)) {
        const attempt = { payments, shipmentId: String(cartQuery.data.shipment?.id),
          paymentId: String(activePaymentId), countryId: String(countryId), locale };
        const previous = lastAutomaticPayment.current;
        if (previous && previous.payments === attempt.payments &&
          previous.shipmentId === attempt.shipmentId && previous.paymentId === attempt.paymentId &&
          previous.countryId === attempt.countryId && previous.locale === attempt.locale) return;
        // Failed automatic selection stays available for an explicit retry, without a request loop.
        lastAutomaticPayment.current = attempt;
        setPaymentTypeMutation.mutate({
          locale,
          paymentId: payments[0].id,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methodsUpdating, payments, paymentState.isReady, pendingShipments, pendingPayments],
  );

  if (paymentState.isError || !payments?.length) {
    return (
      <div role={paymentState.isError ? 'alert' : 'status'} aria-busy={paymentState.isFetching}>
        <p>{t('text1068')}: {t(paymentState.isError ? 'text530' : paymentState.isPending ? 'text439' : 'text477')}</p>
        {paymentState.isError && (
          <Button type="button" disabled={paymentState.fetchStatus !== 'idle'} onClick={() => paymentState.refetch()}>
            {t('text595')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <CartMethodOptions disabled={methodsUpdating}>
      <div className="flex w-full flex-col items-start justify-start">
        {payments?.map(payment => {
          return <CartPayment key={payment.id} payment={payment} />;
        })}
      </div>
    </CartMethodOptions>
  );
}
